import * as React from 'react';
import type { ExecutionResult, FetchResult } from 'react-apollo';
import { Mutation } from 'react-apollo';

import type { DataProxy } from 'apollo-cache';

import applyGradeOverrideMutation from 'bundles/grading/api/ApplyGradeOverrideMutation.graphql';
import deleteGradeOverrideMutation from 'bundles/grading/api/DeleteGradeOverrideMutation.graphql';
import type {
  ApplyGradeOverrideMutation,
  ApplyGradeOverrideMutationVariables,
  DeleteGradeOverrideMutation,
  DeleteGradeOverrideMutationVariables,
} from 'bundles/grading/api/types';
import { ItemType } from 'bundles/grading/api/types';
import type { FetchStatus } from 'bundles/grading/components/staff-graded-assignments/types/SGAGradingTypes';
import { generateSubmissionDataQueryVariables, updateCachedAssignmentGradeData } from 'bundles/grading/utils/apiUtils';

export type GradeOverrideMutationInjectedProps = {
  applyGradeOverride: (param: {
    variables: ApplyGradeOverrideMutationVariables;
  }) => Promise<void | FetchResult<ApplyGradeOverrideMutation, Record<string, $TSFixMe>>>;
  applyGradeOverrideStatus: FetchStatus;
  deleteGradeOverride: (param: {
    variables: DeleteGradeOverrideMutationVariables;
  }) => Promise<void | FetchResult<DeleteGradeOverrideMutation, Record<string, $TSFixMe>>>;
  deleteGradeOverrideStatus: FetchStatus;
};

type Props = {
  children: (gradeOverrideMutations: GradeOverrideMutationInjectedProps) => React.ReactElement | null;
};

/**
 * Feature mutation wrapper handling:
 *  - ApplyGradeOverride mutation
 *  - DeleteGradeOverride mutation
 */
const GradeOverrideMutationWrapper: React.FC<Props> = ({ children }) => {
  const onApplyGradeOverrideMutationComplete = (
    cache: DataProxy,
    { data }: ExecutionResult<ApplyGradeOverrideMutation>
  ) => {
    if (data?.GroupGrading_ApplyGradeOverride) {
      updateCachedAssignmentGradeData(
        cache,
        data,
        generateSubmissionDataQueryVariables(
          data?.GroupGrading_ApplyGradeOverride.submissionReference.groupId,
          data?.GroupGrading_ApplyGradeOverride.submissionReference.itemId,
          ItemType.StaffGradedAssignment,
          data?.GroupGrading_ApplyGradeOverride.submissionReference.submitterId
        )
      );
    }
  };

  const onDeleteGradeOverrideMutationComplete = (
    cache: DataProxy,
    { data }: ExecutionResult<DeleteGradeOverrideMutation>
  ) => {
    if (data?.GroupGrading_DeleteGradeOverride) {
      updateCachedAssignmentGradeData(
        cache,
        data,
        generateSubmissionDataQueryVariables(
          data?.GroupGrading_DeleteGradeOverride.submissionReference.groupId,
          data?.GroupGrading_DeleteGradeOverride.submissionReference.itemId,
          ItemType.StaffGradedAssignment,
          data?.GroupGrading_DeleteGradeOverride.submissionReference.submitterId
        )
      );
    }
  };

  return (
    <Mutation<ApplyGradeOverrideMutation, ApplyGradeOverrideMutationVariables>
      mutation={applyGradeOverrideMutation}
      context={{ clientName: 'gatewayGql' }}
      update={onApplyGradeOverrideMutationComplete}
    >
      {(applyGradeOverride, applyGradeOverrideResult) => (
        <Mutation<DeleteGradeOverrideMutation, DeleteGradeOverrideMutationVariables>
          mutation={deleteGradeOverrideMutation}
          context={{ clientName: 'gatewayGql' }}
          update={onDeleteGradeOverrideMutationComplete}
        >
          {(deleteGradeOverride, deleteGradeOverrideResult) => {
            const applyGradeOverrideStatus: FetchStatus = {
              loading: applyGradeOverrideResult.loading,
              error: applyGradeOverrideResult.error,
            };
            const deleteGradeOverrideStatus: FetchStatus = {
              loading: deleteGradeOverrideResult.loading,
              error: deleteGradeOverrideResult.error,
            };
            const gradeOverrideMutations = {
              applyGradeOverride,
              applyGradeOverrideStatus,
              deleteGradeOverride,
              deleteGradeOverrideStatus,
            };
            return children(gradeOverrideMutations);
          }}
        </Mutation>
      )}
    </Mutation>
  );
};

export default GradeOverrideMutationWrapper;
